import type { ElementType, ReactNode } from 'react';
import type { Link } from 'react-router';
import type { PolymorphicProps } from '#pie/constants/interfaces';
import { tv, type VariantProps } from '#pie/utils/TailwindUtils';
import { Icon, type IconProps } from '../icon/Icon';

export type ButtonProps<TComponent extends ElementType = typeof Link> = {
  iconStart?: IconProps['icon'];
  children?: string | ReactNode;
  iconEnd?: IconProps['icon'];
} & PolymorphicProps<TComponent, VariantProps<typeof button>>;

export const sizeMapper = {
  lg: 24,
  md: 20,
  sm: 16,
} satisfies Record<'lg' | 'md' | 'sm', IconProps['size']>;

export function Button<TComponent extends ElementType = 'button'>({
  as,
  children,
  variant,
  className,
  isLoading,
  to,
  iconStart,
  iconEnd,
  isDisabled,
  size,
  ...props
}: ButtonProps<TComponent>) {
  const Component = as ?? 'button';
  const isIconOnly = (iconStart || iconEnd) && !children;
  const styles = button({ isDisabled, isLoading, size, variant, isIconOnly });

  return (
    <Component
      to={to}
      className={styles.base({ className })}
      disabled={isDisabled || isLoading}
      type={Component === 'button' ? 'button' : undefined}
      {...props}
    >
      {!!isLoading && (
        <div className="absolute animate-spin" role="progressbar" tabIndex={-1}>
          <div className={styles.loader()} />
        </div>
      )}

      {!!iconStart && (
        <Icon
          icon={iconStart}
          size={sizeMapper[size ?? 'lg']}
          className={styles.icon()}
        />
      )}

      {!!children && (
        <span className={styles.childrenWrapper()}>{children}</span>
      )}

      {!!iconEnd && (
        <Icon
          icon={iconEnd}
          size={sizeMapper[size ?? 'lg']}
          className={styles.icon()}
        />
      )}
    </Component>
  );
}

export const button = tv({
  base: 'relative inline-flex items-center justify-center rounded-sm border-transparent bg-transparent text-transparent-reversed gap-2 border whitespace-nowrap',
  defaultVariants: {
    size: 'lg',
    variant: 'primaryBold',
  },
  slots: {
    childrenWrapper: 'inline-flex items-center justify-center',
    icon: 'inline-block',
    loader:
      'absolute left-1/2 top-1/2 hidden h-4 w-4 -translate-x-1/2 -translate-y-1/2 transform rounded-full border-2 border-current border-t-white',
  },
  variants: {
    isDisabled: {
      true: 'pointer-events-none opacity-50',
    },
    isLoading: {
      true: {
        childrenWrapper: 'invisible',
        icon: 'invisible',
        loader: 'block',
      },
    },
    size: {
      lg: 'px-6 py-3',
      md: 'px-4 py-1.5',
      sm: 'px-4 py-1.5',
    },
    isIconOnly: {
      true: 'p-3',
    },
    variant: {
      ghostIcon:
        'bg-transparent border-0 p-1 rounded-sm text-content-body hover:text-action-secondary-enabled focus:text-action-secondary-enabled disabled:text-content-disabled',
      ghostIconPrimary:
        'bg-transparent border-0 p-1 rounded-sm text-content-heading hover:text-action-secondary-enabled focus:text-action-secondary-enabled disabled:text-content-disabled',
      link: {
        base: 'link text-content-link p-0 inline underline decoration-solid underline-offset-2',
        childrenWrapper: 'inline',
        icon: 'align-top',
      },
      linkPrimary: {
        base: 'text-content-link underline p-0 hover:text-underline inline hover:text-action-primary-hover active:text-action-primary-active focus:text-action-primary-focus',
        childrenWrapper: 'inline',
        icon: 'align-top',
      },
      linkSecondary: {
        base: 'link text-action-secondary-enabled p-0 inline hover:text-action-secondary-hover active:text-action-secondary-active focus:text-action-secondary-focus',
        childrenWrapper: 'inline',
        icon: 'align-top',
      },
      primaryBold:
        'bg-action-primary-enabled hover:bg-action-primary-hover active:bg-action-primary-active focus:bg-action-primary-focus disabled:bg-action-primary-disabled border-action-primary-enabled hover:border-action-primary-hover active:border-action-primary-active focus:border-action-primary-focus disabled:border-action-primary-disabled text-content-reversed disabled:text-content-disabled focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-action-primary-focus',
      primarySubtle:
        'text-action-primary-enabled border-action-primary-enabled hover:text-action-primary-hover hover:border-action-primary-hover focus:text-action-primary-focus focus:border-action-primary-focus focus-visible:outline focus-visible:outline-action-primary-focus active:bg-surface-minimal-gray focus:bg-surface-minimal-gray border-action-primary-enabled hover:border-action-primary-hover active:text-action-primary-active active:border-text-action-primary-active disabled:text-content-disabled',
      primaryMinimal:
        'text-action-primary-enabled hover:text-action-primary-hover focus:text-action-primary-focus active:text-action-primary-active disabled:text-content-disabled',
      secondaryBold:
        'bg-action-secondary-enabled hover:bg-action-secondary-hover active:bg-action-secondary-active focus:bg-action-secondary-focus disabled:bg-action-secondary-disabled border-action-secondary-enabled hover:border-action-secondary-hover active:border-action-secondary-active focus:border-action-secondary-focus disabled:border-action-secondary-disabled text-content-reversed disabled:text-content-disabled focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-action-secondary-focus',
      secondarySubtle:
        'text-action-secondary-enabled border-action-secondary-enabled hover:border-action-secondary-hover hover:text-action-secondary-hover focus:border-action-secondary-focus focus:text-action-secondary-focus focus-visible:outline focus-visible:outline-action-secondary-focus active:border-action-secondary-active active:text-action-secondary-active disabled:border-action-secondary-disabled disabled:text-content-disabled',
      secondaryMinimal:
        'text-action-secondary-enabled hover:text-action-secondary-hover hover:bg-surface-minimal-gray focus:text-action-secondary-focus focus:bg-surface-minimal-gray active:text-action-secondary-active active:bg-surface-minimal-gray disabled:text-content-disabled',
      tertiaryBold:
        'bg-action-tertiary-enabled border-action-tertiary-enabled hover:bg-action-tertiary-hover hover:border-action-tertiary-hover active:bg-action-tertiary-active active:border-action-tertiary-active focus:bg-action-tertiary-focus focus:border-action-tertiary-focus disabled:bg-action-tertiary-disabled disabled:border-action-tertiary-disabled text-content-body hover:text-content-reversed active:text-content-body focus:text-content-body disabled:text-content-disabled focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-action-tertiary-focus',
      tertiarySubtle:
        'hover:bg-surface-disabled active:bg-surface-minimal-gray focus:bg-surface-subtle-gray border-action-tertiary-enabled hover:border-action-tertiary-hover focus:border-action-tertiary-focus focus-visible:outline focus-visible:outline-action-tertiary-focus active:border-action-tertiary-active disabled:border-action-tertiary-disabled text-content-body disabled:text-content-disabled',
      tertiaryMinimal:
        'hover:bg-surface-disabled focus:bg-surface-disabled text-content-body disabled:text-content-disabled',
      tertiaryMinimalIcon: {
        base: 'hover:bg-surface-disabled focus:bg-surface-disabled text-content-body disabled:text-content-disabled',
        icon: 'text-action-secondary-enabled',
      },
      tertiarySubtleIcon: {
        base: 'hover:bg-surface-disabled active:bg-surface-minimal-gray focus:bg-surface-subtle-gray border-action-tertiary-enabled hover:border-action-tertiary-hover focus:border-action-tertiary-focus active:border-action-tertiary-active focus-visible:outline focus-visible:outline-action-tertiary-focus disabled:border-action-tertiary-disabled text-content-body disabled:text-content-disabled',
        icon: 'text-action-secondary-enabled',
      },
      errorBold:
        'bg-support-error-bold border-support-error-bold text-content-reversed focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-support-error-bold',
      errorSubtle:
        'bg-surface-base hover:bg-surface-minimal-gray active:bg-surface-minimal-gray focus:bg-surface-minimal-gray border-support-error-bold focus-visible:outline focus-visible:outline-support-error-bold text-content-error',
      errorMinimal:
        'text-content-error hover:bg-surface-minimal-gray focus:bg-surface-minimal-gray',
      disabled:
        'bg-surface-minimal-gray border-border-disabled text-content-disabled',
    },
  },
});
